import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import PaymentForm from "../PaymentForm";
import { useCreateSetupIntent } from "../../services/queries";
import { Box, Typography, Button, Paper, CircularProgress } from "@mui/material";
import ErrorWheel from "../ErrorWheel";

// Deferred singleton: loadStripe() creates Stripe's own cross-origin iframes
// (js.stripe.com/m.stripe.network) as a side effect. Calling it at module
// scope fired on every import of this file — including via the static
// Settings/index.jsx -> Billing/index.jsx -> AddPayment import chain, so it
// ran on ANY Settings tab visit (Account, Password, ...), not just Billing.
// WebKit's stricter cross-origin frame-access checks turned that into a
// visible console error flood (xe-0914-dev-webkit-cross-origin-frame-error-flood)
// even though the payment form was never opened. Deferring the call until
// this component actually renders means it only fires when a user reaches
// the payment form.
// The default "@stripe/stripe-js" entry point ALSO auto-injects the
// js.stripe.com/v3 script itself (a `Promise.resolve().then(...)` fired at
// module-import time, independent of ever calling loadStripe()) -- so simply
// deferring this function's own call was not enough: the eager network load
// survived via that import-time side effect, live-measured firing on the
// Settings > Account tab within 500ms of page load (main.570bbf0f.js,
// 2026-09-16). "@stripe/stripe-js/pure" is Stripe's documented side-effect-free
// entry point; it only injects the script when loadStripe() is actually called.
let stripePromise;
function getStripePromise() {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
  }
  return stripePromise;
}

export default function AddPayment({ closeModal }) {
  const { data, isLoading, isError, error } = useCreateSetupIntent();
  const clientSecret = data?.data?.client_secret || "";

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap={2}
        minHeight={220}
        py={4}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Preparing secure payment…
        </Typography>
      </Box>
    );
  }

  if (isError || !clientSecret) {
    return (
      <Paper sx={{ p: 3, maxWidth: 560, margin: "24px auto" }} elevation={3}>
        <Box display="flex" alignItems="center" gap={2}>
          {/* Shared ErrorWheel at the glyph's original 44px so the row metrics
              are unchanged. Replaces a MUI ErrorOutline with MUI's own
              `color="error"` red — an off-brand mark and a red the design
              system never named. */}
          <ErrorWheel size={44} decorative />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Payment initialization failed
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              {error?.message ||
                "We couldn't initialize the payment form right now. Please try refreshing the page or try again later."}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Elements stripe={getStripePromise()} options={{ clientSecret }}>
      <PaymentForm onCancel={closeModal} />
    </Elements>
  );
}
